import { SalesStats } from 'src/app/models/core/chart-interfaces';
import { AmountInput } from 'src/app/utils/currency-format';

type ReportData = Record<string, unknown>;

/** La API a veces envuelve el payload en `data` y a veces no. */
export function unwrapReportData(res: unknown): ReportData {
  if (!res || typeof res !== 'object') return {};
  const record = res as ReportData;
  const data = record['data'];
  return data && typeof data === 'object' ? (data as ReportData) : record;
}

/** Cifra para mostrar: `null`/faltante cuenta como 0 (la API manda null a veces). */
const count = (value: unknown): string => String(value ?? 0);
const percent = (value: unknown): number => Number(value ?? 0) || 0;

const stat = (titleKey: string, value: unknown, percentage: unknown, clickable: boolean): SalesStats => ({
  titleKey,
  value: count(value),
  percentage: percent(percentage),
  clickable,
});

export interface EventKpis {
  readonly eventName: string;
  /** Crudo: puede venir como string; `formatAmount` decide cómo mostrarlo. */
  readonly totalAmount: AmountInput;
  readonly stats: SalesStats[];
}

/**
 * KPIs de ventas de un evento (`report_my_sales`), igual para Jefe/Admin y Vendedor:
 * las 4 principales navegan a Asistentes; las de tickets físicos (carga masiva)
 * solo aparecen si el evento los tiene y no navegan.
 */
export function mapEventKpis(raw: unknown): EventKpis {
  const d = unwrapReportData(raw);
  const stats: SalesStats[] = [
    stat('reports-single-event.tickets-sold', d['total_tickets'], 100, true),
    stat('reports-single-event.registered', d['total_assistants'], 100, true),
    stat('reports-single-event.verified', d['verified_assistants'], d['verified_percentage'], true),
    stat('reports-single-event.absent', d['absent_assistants'], d['absent_percentage'], true),
  ];

  if (Number(d['physical_registered'] ?? 0) > 0) {
    stats.push(
      stat('reports-single-event.physical-tickets', d['physical_tickets'], 100, false),
      stat('reports-single-event.physical-registered', d['physical_registered'], 100, false),
      stat('reports-single-event.physical-verified', d['physical_verified'], d['physical_verified_percentage'], false),
      stat('reports-single-event.physical-absent', d['physical_absent'], d['physical_absent_percentage'], false),
    );
  }

  return {
    eventName: String(d['event_name'] ?? ''),
    // Solo el faltante es 0: un null llega a formatAmount y se muestra "—", como hoy.
    totalAmount: (d['total_amount'] === undefined ? 0 : d['total_amount']) as AmountInput,
    stats,
  };
}

export interface AttendanceKpis {
  readonly totalTickets: number;
  readonly stats: SalesStats[];
}

/** KPIs de asistencia (recepcionista/scanner): total de QRs y 3 métricas sin navegación. */
export function mapAttendanceKpis(raw: unknown): AttendanceKpis {
  const d = unwrapReportData(raw);
  const totalTickets = Number(d['total_tickets'] ?? 0);
  return {
    totalTickets: Number.isFinite(totalTickets) ? totalTickets : 0,
    stats: [
      stat('reports-single-event.registered', d['total_assistants'], 100, false),
      stat('reports-single-event.verified', d['verified_assistants'], d['verified_percentage'], false),
      stat('reports-single-event.absent', d['absent_assistants'], d['absent_percentage'], false),
    ],
  };
}
